import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Users, Award, Target, TrendingUp, Clock, Shield } from "lucide-react";

const About = () => {
  const values = [
    {
      title: "Mobilité",
      description: "Nous venons directement chez vous, où que vous soyez au Sénégal.",
      icon: Award,
    },
    {
      title: "Sécurité",
      description: "Garantir la sécurité routière et la fiabilité des véhicules en circulation.",
      icon: Target,
    },
    {
      title: "Conformité",
      description: "Respect strict des exigences réglementaires et des normes en vigueur.",
      icon: Shield,
    },
    {
      title: "Modernité",
      description: "Solution innovante et rapide adaptée aux besoins contemporains.",
      icon: Clock,
    },
  ];

  const stats = [
    { number: "100%", label: "Service mobile" },
    { number: "14", label: "Régions couvertes" },
    { number: "1000+", label: "Véhicules contrôlés" },
    { number: "24h", label: "Délai de réponse" },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-indigo-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-6">
            <Badge variant="secondary" className="w-fit mx-auto">
              Qui sommes-nous ?
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
              CLERYS - Service Mobile de Contrôle Technique
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              "Nous venons chez vous pour garantir votre sécurité et celle des autres."
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Notre mission
              </h2>
              <p className="text-lg text-gray-600">
                CLERYS est une société sénégalaise spécialisée dans le contrôle technique mobile 
                de véhicules, agréée par les autorités compétentes (Direction des Transports Terrestres).
              </p>
              <p className="text-lg text-gray-600">
                Notre mission est de faciliter l'accès au contrôle technique en nous déplaçant 
                directement chez nos clients, particuliers comme professionnels, partout au Sénégal.
              </p>
              <p className="text-lg text-gray-600">
                Nous apportons une solution moderne, rapide et conforme aux exigences réglementaires 
                pour garantir la sécurité routière et la fiabilité des véhicules en circulation.
              </p>
            </div>
            <div>
              <img
                src="/images/clients_africains_1.jpeg"
                alt="Technicien africain professionnel"
                className="rounded-lg shadow-lg w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Nos chiffres clés
            </h2>
            <p className="text-xl text-gray-600">
              Des résultats qui témoignent de notre engagement et de notre expertise.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-primary mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-medium">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Advantages Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Nos atouts concurrentiels
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              Découvrez ce qui fait la différence avec CLERYS et pourquoi choisir 
              notre service mobile de contrôle technique.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                title: "Service mobile près de chez vous",
                description: "Stationnement facile, pas besoin de chercher une place dans un centre encombré. Nous venons directement à votre domicile ou bureau.",
                icon: Target
              },
              {
                title: "Pas de perte de temps",
                description: "Fini les files d'attente interminables ! Votre temps est précieux, nous respectons vos horaires et votre planning.",
                icon: Clock
              },
              {
                title: "Équipe technique certifiée",
                description: "Nos techniciens sont formés et certifiés selon les standards les plus élevés pour garantir un service professionnel.",
                icon: Award
              },
              {
                title: "Équipement de pointe",
                description: "Nous utilisons des équipements mobiles de dernière génération pour des contrôles précis et fiables.",
                icon: TrendingUp
              },
              {
                title: "Respect des normes en vigueur",
                description: "Conformité stricte avec toutes les réglementations sénégalaises et internationales en matière de contrôle technique.",
                icon: Shield
              },
              {
                title: "Ponctualité garantie",
                description: "Nous venons à l'adresse indiquée, à l'heure convenue. Votre rendez-vous est notre priorité.",
                icon: Users
              },
              {
                title: "Prise de rendez-vous rapide",
                description: "Processus de réservation simplifié et rapide pour s'adapter à votre emploi du temps chargé.",
                icon: Target
              },
              {
                title: "Accueil chaleureux",
                description: "Conseils personnalisés et accompagnement bienveillant tout au long du processus de contrôle.",
                icon: Award
              }
            ].map((advantage, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <advantage.icon className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle className="text-lg">{advantage.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">
                    {advantage.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Nos valeurs
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ces valeurs fondamentales guident notre travail quotidien et nos relations 
              avec nos clients et partenaires.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <value.icon className="h-8 w-8 text-primary" />
                  </div>
                  <CardTitle className="text-xl">{value.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">
                    {value.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Notre équipe
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Une équipe de professionnels passionnés et expérimentés, dédiée à votre succès.
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="/images/equipe_africaine_3.jpeg"
                alt="Mécanicien africain inspectant un véhicule"
                className="rounded-lg shadow-lg w-full h-auto"
              />
            </div>
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-gray-900">
                Une expertise multidisciplinaire
              </h3>
              <p className="text-lg text-gray-600">
                Notre équipe réunit des experts dans différents domaines : stratégie d'entreprise, 
                finance, ressources humaines, et transformation digitale. Cette diversité nous 
                permet d'offrir une approche holistique à nos clients.
              </p>
              <p className="text-lg text-gray-600">
                Chaque membre de notre équipe apporte une expérience unique, acquise dans des 
                secteurs variés et des entreprises de toutes tailles. Cette richesse d'expérience 
                nous permet de comprendre rapidement vos enjeux et de vous proposer des solutions 
                adaptées.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;